"use client";
import React, { ReactElement, useMemo } from "react";

import { ClassNameMap } from "@mui/material";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import clsx from "clsx";

import "swiper/css/bundle";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { Swiper, SwiperSlide } from "~/lib/swiper/component";

import { useLazySwiper } from "../../hooks/use-lazy-swiper";
import { useSwiper } from "../../hooks/use-swiper";
import {
  DESKTOP_SLICES_PER_VIEW,
  MOBILE_SLICES_PER_VIEW,
} from "../../hooks/use-swiper/constants";
import { NavigationButtons } from "../navigation-buttons/component";

import { PRODUCT_CAROUSEL_CLASSES, SLIDE_STEP } from "./constants";
import { ProductCarouselContainer } from "./styled";

export interface ProductCarouselProps {
  dataId?: string;
  header?: ReactElement;
  layout?: "left" | "right";
  classes?: Partial<ClassNameMap>;
  children: React.ReactNode;
  carouselType?: "default" | "newIn";
}

export const ProductCarousel: React.FC<ProductCarouselProps> = ({
  children,
  header,
  layout = "left",
  carouselType = "default",
  classes: externalClasses,
  dataId,
}) => {
  const theme = useTheme();
  const isRightLayout = useMemo(() => layout === "right", [layout]);
  const isNewInCarousel = useMemo(() => carouselType === "newIn", [carouselType]);
  const {
    setSwiper,
    isNextEnabled,
    isPrevEnabled,
    windowWidth,
    handleSliceChange,
    handleNextClick,
    handlePrevClick,
    handleElementFocus,
    swiper,
  } = useSwiper(SLIDE_STEP, true);

  const { carouselRef, swiperSlices } = useLazySwiper(
    swiper,
    children,
    handleSliceChange,
    isRightLayout,
    isNewInCarousel,
  );

  return (
    <ProductCarouselContainer
      id={dataId}
      isRightLayout={isRightLayout}
      isNewInCarousel={isNewInCarousel}
      className={externalClasses?.root}
      {...getTestAutomationProps("carousel")}
    >
      {header ? (
        <Box
          className={clsx(PRODUCT_CAROUSEL_CLASSES.header, externalClasses?.header)}
          {...getTestAutomationProps("header-block")}
        >
          {header}
        </Box>
      ) : undefined}
      <Box
        className={clsx(PRODUCT_CAROUSEL_CLASSES.swiper, externalClasses?.swiper)}
        ref={carouselRef}
      >
        {isRightLayout ? (
          <NavigationButtons
            classes={{
              root: clsx(PRODUCT_CAROUSEL_CLASSES.buttons, externalClasses?.buttons),
              button: clsx(PRODUCT_CAROUSEL_CLASSES.button, externalClasses?.button),
              buttonLeft: clsx(
                PRODUCT_CAROUSEL_CLASSES.buttonLeft,
                externalClasses?.buttonLeft,
              ),
              buttonRight: clsx(
                PRODUCT_CAROUSEL_CLASSES.buttonRight,
                externalClasses?.buttonRight,
              ),
            }}
            leftButtonProps={{
              disabled: !isPrevEnabled,
              onClick: handlePrevClick,
            }}
            rightButtonProps={{
              disabled: !isNextEnabled,
              onClick: handleNextClick,
            }}
          />
        ) : undefined}
        <Box
          className={clsx(
            PRODUCT_CAROUSEL_CLASSES.container,
            externalClasses?.container,
          )}
        >
          <Box
            className={clsx(
              PRODUCT_CAROUSEL_CLASSES.elements,
              externalClasses?.elements,
            )}
            {...getTestAutomationProps("elements")}
          >
            <Swiper
              key={windowWidth}
              initialSlide={!isRightLayout ? React.Children.count(swiperSlices) : 0}
              spaceBetween={0}
              onSwiper={setSwiper}
              onSlideChange={handleSliceChange}
              slidesPerView={DESKTOP_SLICES_PER_VIEW}
              breakpoints={{
                [theme.breakpoints.values.sm]: {
                  slidesPerView: MOBILE_SLICES_PER_VIEW,
                },
              }}
            >
              {!isRightLayout && !isNewInCarousel ? (
                <SwiperSlide
                  className={clsx(
                    PRODUCT_CAROUSEL_CLASSES.slider,
                    externalClasses?.slider,
                  )}
                  {...getTestAutomationProps("element")}
                />
              ) : undefined}
              {React.Children.map(swiperSlices, (element, index) => (
                <SwiperSlide
                  className={clsx(
                    PRODUCT_CAROUSEL_CLASSES.slider,
                    externalClasses?.slider,
                  )}
                  onFocus={() => handleElementFocus(index)}
                >
                  <Box
                    className={clsx(
                      PRODUCT_CAROUSEL_CLASSES.element,
                      externalClasses?.element,
                    )}
                    {...getTestAutomationProps("element")}
                  >
                    {element}
                  </Box>
                </SwiperSlide>
              ))}
              {isRightLayout || isNewInCarousel ? (
                <SwiperSlide
                  className={clsx(
                    PRODUCT_CAROUSEL_CLASSES.slider,
                    externalClasses?.slider,
                  )}
                  {...getTestAutomationProps("element")}
                />
              ) : undefined}
            </Swiper>
          </Box>
        </Box>
        {!isRightLayout ? (
          <NavigationButtons
            classes={{
              root: clsx(PRODUCT_CAROUSEL_CLASSES.buttons, externalClasses?.buttons),
              button: clsx(PRODUCT_CAROUSEL_CLASSES.button, externalClasses?.button),
              buttonLeft: clsx(
                PRODUCT_CAROUSEL_CLASSES.buttonLeft,
                externalClasses?.buttonLeft,
              ),
              buttonRight: clsx(
                PRODUCT_CAROUSEL_CLASSES.buttonRight,
                externalClasses?.buttonRight,
              ),
            }}
            leftButtonProps={{
              disabled: !isPrevEnabled,
              onClick: handlePrevClick,
            }}
            rightButtonProps={{
              disabled: !isNextEnabled,
              onClick: handleNextClick,
            }}
          />
        ) : undefined}
      </Box>
    </ProductCarouselContainer>
  );
};
